'use client';

export type CaptchaProvider = 'recaptcha' | 'turnstile';

export interface CaptchaResult {
  token: string;
  provider: CaptchaProvider;
}

declare global {
  interface Window {
    grecaptcha?: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
    turnstile?: {
      render: (container: HTMLElement, options: Record<string, unknown>) => string;
      execute: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

const provider: CaptchaProvider = process.env.NEXT_PUBLIC_CAPTCHA_PROVIDER === 'turnstile'
  ? 'turnstile'
  : 'recaptcha';
const recaptchaSiteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || '';
const turnstileSiteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || '';

let scriptPromise: Promise<void> | null = null;
let managedScript: HTMLScriptElement | null = null;

function loadScript(src: string, ready: () => boolean): Promise<void> {
  if (ready()) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`) as HTMLScriptElement | null;
    const ownsScript = !existing;
    const script = existing || document.createElement('script');
    let timer: number | undefined;
    const fail = (message: string) => {
      if (timer) window.clearTimeout(timer);
      if (ownsScript && managedScript === script) {
        script.remove();
        managedScript = null;
      }
      reject(new Error(message));
    };
    const complete = () => {
      if (timer) window.clearTimeout(timer);
      if (!ready()) return fail('La protección anti-spam no está disponible.');
      resolve();
    };
    if (!existing) {
      script.src = src;
      script.async = true;
      script.defer = true;
      managedScript = script;
      document.head.appendChild(script);
    }
    script.addEventListener('load', complete, { once: true });
    script.addEventListener('error', () => fail('No se pudo cargar la protección anti-spam.'), { once: true });
    timer = window.setTimeout(() => fail('La protección anti-spam ha tardado demasiado en cargar.'), 15000);
  });
  scriptPromise.catch(() => { scriptPromise = null; });
  return scriptPromise;
}

async function getRecaptchaToken(action: string): Promise<string> {
  if (!recaptchaSiteKey) throw new Error('La protección anti-spam no está configurada. Inténtalo más tarde.');
  await loadScript(`https://www.google.com/recaptcha/api.js?render=${recaptchaSiteKey}`, () => Boolean(window.grecaptcha));
  if (!window.grecaptcha) throw new Error('La protección anti-spam no está disponible.');
  await new Promise<void>((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error('La protección anti-spam ha tardado demasiado.')), 15000);
    window.grecaptcha?.ready(() => { window.clearTimeout(timer); resolve(); });
  });
  const token = await Promise.race([
    window.grecaptcha.execute(recaptchaSiteKey, { action }),
    new Promise<never>((_, reject) => window.setTimeout(() => reject(new Error('La protección anti-spam ha tardado demasiado.')), 15000)),
  ]);
  if (!token) throw new Error('No pudimos validar la protección anti-spam.');
  return token;
}

async function getTurnstileToken(action: string): Promise<string> {
  if (!turnstileSiteKey) throw new Error('La protección anti-spam no está configurada. Inténtalo más tarde.');
  await loadScript('https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit', () => Boolean(window.turnstile));
  const turnstile = window.turnstile;
  if (!turnstile) throw new Error('La protección anti-spam no está disponible.');

  return new Promise<string>((resolve, reject) => {
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const overlay = document.createElement('dialog');
    overlay.setAttribute('aria-label', 'Verificación anti-spam');
    // Radix modals set pointer-events:none on body; the native dialog must opt back in.
    overlay.style.cssText = 'pointer-events:auto;z-index:2147483647;width:min(calc(100% - 32px),420px);border:0;border-radius:12px;padding:24px;box-shadow:0 25px 50px rgba(0,0,0,.25);';
    const panel = document.createElement('div');
    panel.style.cssText = 'width:min(100%,420px);border-radius:12px;background:#fff;padding:24px;box-shadow:0 25px 50px rgba(0,0,0,.25);';
    const title = document.createElement('p');
    title.textContent = 'Confirma que eres una persona para enviar la solicitud.';
    title.style.cssText = 'margin:0 0 16px;color:#111827;font:500 14px system-ui,sans-serif;';
    const container = document.createElement('div');
    const cancel = document.createElement('button');
    cancel.type = 'button';
    cancel.textContent = 'Cancelar';
    cancel.style.cssText = 'margin-top:16px;border:0;background:transparent;color:#4b5563;font:500 14px system-ui,sans-serif;cursor:pointer;';
    panel.append(title, container, cancel);
    overlay.appendChild(panel);
    // Keep clicks/focus in this top-layer dialog from dismissing a parent Radix form.
    overlay.addEventListener('pointerdown', event => event.stopPropagation());
    overlay.addEventListener('focusin', event => event.stopPropagation());
    document.body.appendChild(overlay);
    overlay.showModal();
    cancel.focus();
    let widgetId = '';
    let settled = false;
    const onEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      finish(new Error('Has cancelado la validación anti-spam.'));
    };
    const cleanup = () => {
      window.removeEventListener('keydown', onEscape, true);
      if (widgetId) turnstile.remove(widgetId);
      if (overlay.open) overlay.close();
      overlay.remove();
      previousFocus?.focus();
    };
    const finish = (error?: Error, token?: string) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      cleanup();
      error ? reject(error) : resolve(token!);
    };
    const timer = window.setTimeout(() => {
      finish(new Error('La validación anti-spam ha tardado demasiado. Inténtalo de nuevo.'));
    }, 120000);
    window.addEventListener('keydown', onEscape, true);
    cancel.addEventListener('click', () => finish(new Error('Has cancelado la validación anti-spam.')));
    overlay.addEventListener('cancel', (event) => {
      event.preventDefault();
      finish(new Error('Has cancelado la validación anti-spam.'));
    });
    try {
      widgetId = turnstile.render(container, {
        sitekey: turnstileSiteKey,
        size: 'flexible',
        execution: 'execute',
        appearance: 'interaction-only',
        action,
        callback: (token: string) => finish(token ? undefined : new Error('No pudimos validar la protección anti-spam.'), token),
        'error-callback': () => finish(new Error('No pudimos validar la protección anti-spam.')),
        'expired-callback': () => finish(new Error('La validación anti-spam ha caducado. Inténtalo de nuevo.')),
      });
      turnstile.execute(widgetId);
    } catch {
      finish(new Error('No pudimos iniciar la validación anti-spam.'));
    }
  });
}

/** Obtiene un token justo antes de enviar un formulario. Nunca permite enviar sin token. */
export async function getCaptcha(action: string): Promise<CaptchaResult> {
  if (typeof window === 'undefined') throw new Error('La protección anti-spam solo está disponible en el navegador.');
  const token = provider === 'turnstile'
    ? await getTurnstileToken(action)
    : await getRecaptchaToken(action);
  return { token, provider };
}

export function getCaptchaProvider(): CaptchaProvider {
  return provider;
}
